import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../constants/color";
import { Icon } from "../icons";
import { url } from "../constants/url";
import axios from "axios";
import { useAuth } from "../context/authContext";
import { useFlowToggle } from "../context/flowToggleContext";
import { useNavigate } from "react-router-dom";
import CustomTooltip from "../components/CustomTooltip";
import ClientDetailsSidebar from "../components/clients/ClientDetailsSidebar";
import ClientDetailsPopup from "../components/clients/ClientDetailsPopup";
import ClientStatusUpdateModal from "../components/clients/ClientStatusUpdateModal";
import ClientDeleteModal from "../components/clients/ClientDeleteModal";
import AddCreditsModal from "../components/clients/AddCreditsModal";
import EditCreditsModal from "../components/clients/EditCreditsModal";
import AppToast from "../components/AppToast";

const statusStyles = {
  Active: {
    bgcolor: "#8CD47E",
    color: "#ffffff",
  },
  APPROVED: {
    bgcolor: "#8CD47E",
    color: "#ffffff",
  },
  Pending: {
    bgcolor: "#F8D66D",
    color: "#ffffff",
  },
  PENDING: {
    bgcolor: "#F8D66D",
    color: "#ffffff",
  },
  Rejected: {
    bgcolor: "#FF6961",
    color: "#ffffff",
  },
  REJECTED: {
    bgcolor: "#FF6961",
    color: "#ffffff",
  },
};

const getRejectedCellStyle = (status) => {
  return status === "REJECTED" ? {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      top: '50%',
      borderTop: '1px solid #FF6961',
      zIndex: 1,
      pointerEvents: 'none'
    }
  } : {};
};

const Clients = () => {
  const { token } = useAuth();
  const { isTechnicalFlow } = useFlowToggle();
  const navigate = useNavigate();
  const [clientsData, setClientsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const [pageSize] = useState(12);
  const [viewAnchorEl, setViewAnchorEl] = useState(null);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [nameSortAnchorEl, setNameSortAnchorEl] = useState(null);
  const [companySortAnchorEl, setCompanySortAnchorEl] = useState(null);
  const [selectedView, setSelectedView] = useState("ALL");
  const [selectedSort, setSelectedSort] = useState("LATEST");
  const [nameSort, setNameSort] = useState(null);
  const [companySort, setCompanySort] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [addCreditsOpen, setAddCreditsOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [editCreditsOpen, setEditCreditsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Remove old snackbar state
  // const [snackbar, setSnackbar] = useState({
  //   open: false,
  //   message: "",
  //   severity: "success",
  // });

  // Add toast state at the parent level
  const [toast, setToast] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  // Helper function to show toast
  const showToast = (type, title, message) => {
    setToast({
      open: true,
      type: type,
      title: title,
      message: message,
    });
  };

  // Helper function to hide toast
  const hideToast = () => {
    setToast({ ...toast, open: false });
  };

  const [deletedClientIds, setDeletedClientIds] = useState([]);

  const handleAddCreditsClick = (client) => {
    setSelectedClient(client);
    setAddCreditsOpen(true);
  };

  const handleViewDetails = (client) => {
    setSelectedClient(client);
    if (isTechnicalFlow) {
      setIsPopupOpen(true);
    } else {
      setIsSidebarOpen(true);
    }
  };

  const handleDeleteClick = (client) => {
    setSelectedClient(client);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async (client) => {
    if (!client || !client._id) {
      console.error("No client ID found for deletion");
      showToast("error", "Error", "Cannot remove client - no ID found");
      setDeleteOpen(false);
      return;
    }

    const clientName = client.name || "Client";

    try {
      setActionLoading(client._id + "delete");
      console.log("Deleting client:", client._id);

      // Call backend API to delete user
      const response = await axios.delete(
        `${url}/api/users/delete/${client._id}`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );

      console.log("Delete response:", response.data);

      if (response.data.success || response.status === 200) {

        // Remove from current view
        setClientsData((prevClients) =>
          prevClients.filter((c) => c._id !== client._id)
        );

        // Update total count
        setTotalClients((prev) => Math.max(0, prev - 1));

        // Adjust page if needed
        if (clientsData.length === 1 && page > 1) {
          setPage((prev) => prev - 1);
        }

        // Show success message with name
        showToast(
          "success",
          "Client Deleted",
          `<strong>${clientName}</strong> has been permanently deleted from the database`
        );
      }
    } catch (error) {
      console.error("Error deleting client:", error);
      console.error("Error response:", error.response?.data);
      const clientName = client?.name || "Client";
      showToast(
        "error",
        "Error",
        `Error deleting ${clientName}: ${error.response?.data?.message || error.message
        }`
      );
    } finally {
      setActionLoading(null);
      setDeleteOpen(false);
      setSelectedClient(null);
    }
  };

  const handleCloseSidebar = () => {
    setSelectedClient(null);
    setIsSidebarOpen(false);
  };

  const mapSortToApi = (sortValue) => {
    switch (sortValue) {
      case "LATEST":
        return "latest";
      case "OLDEST":
        return "oldest";
      case "AZ":
        return "az";
      case "ZA":
        return "za";
      case "CREDITS_HIGH":
        return "creditsHigh";
      case "CREDITS_LOW":
        return "creditsLow";
      default:
        return "latest";
    }
  };

  const mapViewToApi = (viewValue) => {
    switch (viewValue) {
      case "APPROVED":
        return "APPROVED";
      case "PENDING":
        return "PENDING";
      case "REJECTED":
        return "REJECTED";
      case "ALL":
        return null;
      default:
        return null;
    }
  };

  const isFilterApplied = () => {
    return (
      selectedView !== "ALL" ||
      selectedSort !== "LATEST" ||
      nameSort !== null ||
      companySort !== null ||
      searchQuery !== ""
    );
  };

  // Fetch clients from API
  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        role: "CLIENT",
        page: page,
        limit: pageSize,
      };

      if (isTechnicalFlow) {
        params.roleType = "Individual";
      } else {
        params.excludeRoleType = "Individual";
      }

      if (selectedSort !== "LATEST") {
        params.sort = mapSortToApi(selectedSort);
      }

      if (nameSort === "AZ") {
        params.sort = "az";
      } else if (nameSort === "ZA") {
        params.sort = "za";
      } else if (companySort === "AZ") {
        params.sort = "az";
      } else if (companySort === "ZA") {
        params.sort = "za";
      }

      const statusFilter = mapViewToApi(selectedView);
      if (statusFilter) {
        params.status = statusFilter;
      }
      
      if (searchQuery) {
        params.search = searchQuery;
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: params,
      };

      const response = await axios.get(`${url}/api/users/fetch`, config);

      if (response.data.users) {
        setClientsData(response.data.users);
        
        // Use total from backend response
        const totalFromBackend = response.data.total || 0;

        setTotalClients(Math.max(0, totalFromBackend));
        setTotalPages(Math.ceil(totalFromBackend / pageSize));
      } else {
        setClientsData([]);
        setTotalClients(0);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Error fetching clients:", err);
      setError("Failed to fetch clients data");
      showToast(
        "error",
        "Fetch Failed",
        "Failed to fetch clients data. Please try again."
      );
      setClientsData([]);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    pageSize,
    token,
    selectedSort,
    selectedView,
    nameSort,
    companySort,
    searchQuery,
    isTechnicalFlow,
  ]);

  // API call to update user status
  const updateUserStatus = async (userId, newStatus) => {
    try {
      setActionLoading(true);

      // Find client for name
      const client = clientsData.find((c) => c._id === userId);
      const clientName = client?.name || "Client";
      const currentStatus = getStatusText(client?.status || "PENDING");

      // Map frontend status to backend status
      const statusMap = {
        Active: "APPROVED",
        Rejected: "REJECTED",
        Pending: "PENDING",
      };

      const backendStatus = statusMap[newStatus] || newStatus.toUpperCase();

      const response = await axios.post(
        `${url}/api/admin/approve-req`,
        {
          userID: userId,
          action: backendStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        showToast(
          "success",
          "Status Updated",
          `<strong>${clientName}'s</strong> status changed from <strong>${currentStatus}</strong> to <strong>${newStatus}</strong>`
        );

        // Refresh the clients list
        fetchClients();

        // Close the modal
        setStatusModalOpen(false);
        setSelectedClient(null);
      }
    } catch (err) {
      console.error("Error updating user status:", err);
      const client = clientsData.find((c) => c._id === userId);
      const clientName = client?.name || "Client";
      showToast(
        "error",
        "Update Failed",
        `Failed to update <strong>${clientName}'s</strong> status: ${err.response?.data?.message || err.message
        }`
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Handle status update from modal
  const handleStatusUpdate = (selectedStatus) => {
    if (selectedClient) {
      updateUserStatus(selectedClient._id, selectedStatus);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Calculate displayed range
  const startIndex = (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, totalClients);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).replace(/\//g, '/')
  }

  // Get status text for display
  const getStatusText = (status) => {
    switch (status) {
      case "APPROVED":
        return "Active";
      case "PENDING":
        return "Pending";
      case "REJECTED":
        return "Rejected";
      default:
        return status || "Pending";
    }
  };

  // Calculate remaining credits
  const calculateRemainingCredits = (client) => {
    const used = client.credits?.usedCredits || 0;
    const remaining = client.credits?.totalCredits || 0;
    const total = used + remaining;

    const outstationUsed = client.credits?.outstationCredits?.usedCredits || 0;
    const outstationRemaining = client.credits?.outstationCredits?.totalCredits || 0;
    const outstationTotal = outstationUsed + outstationRemaining;

    return {
      normal: {
        remaining: remaining,
        total: total,
        used: used,
        display: (total === 0 && used === 0) ? "-" : `${remaining}/${total}`,
        hasCredits: (total > 0 || used > 0),
      },
      outstation: {
        remaining: outstationRemaining,
        total: outstationTotal,
        used: outstationUsed,
        display: (outstationTotal === 0 && outstationUsed === 0) ? "-" : `${outstationRemaining}/${outstationTotal}`,
        hasCredits: (outstationTotal > 0 || outstationUsed > 0),
      }
    };
  };

  // Remove old snackbar close handler
  // const handleCloseSnackbar = () => {
  //   setSnackbar({ ...snackbar, open: false });
  // };

  // Clear all filters
  const clearFilters = () => {
    setSelectedView("ALL");
    setSelectedSort("LATEST");
    setNameSort(null);
    setCompanySort(null);
    setSearchQuery("");
    setPage(1);
  };

  // Handle Name column sort
  const handleNameSort = (sortType) => {
    setNameSort(sortType);
    setCompanySort(null);
    setNameSortAnchorEl(null);
    setPage(1);
  };

  // Handle Company column sort
  const handleCompanySort = (sortType) => {
    setCompanySort(sortType);
    setNameSort(null);
    setCompanySortAnchorEl(null);
    setPage(1);
  };

  // Get frontend status from backend status
  const getFrontendStatus = (backendStatus) => {
    switch (backendStatus) {
      case "APPROVED":
        return "Active";
      case "PENDING":
        return "Pending";
      case "REJECTED":
        return "Rejected";
      default:
        return backendStatus || "Pending";
    }
  };

  // Function to restore all deleted clients (optional - for admin use)
  const restoreAllDeletedClients = () => {
    // This function is no longer supported as clients are hard-deleted
    showToast(
      "error",
      "Restore Failed",
      "Clients are permanently deleted and cannot be restored"
    );
  };

  if (loading && clientsData.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography
            sx={{
              ...TYPOGRAPHY.H2,
              fontFamily: FONT_FAMILY,
              color: COLORS.PRIMARY,
            }}
          >
            Clients
          </Typography>

          {!isTechnicalFlow && (
            <Button
              startIcon={<Icon name="plus2" />}
              onClick={() => handleAddCreditsClick(null)}
              sx={{
                bgcolor: COLORS.SECONDARY,
                color: "#fff",
                textTransform: "none",
                borderRadius: 2,
                ...TYPOGRAPHY.H4,
                px: 2.5,
                "&:hover": { bgcolor: COLORS.SECONDARY },
              }}
            >
              Add Credits
            </Button>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') fetchClients(); }}
            placeholder="Search..."
            size="small"
            variant="outlined"
            sx={{
              background: "#fff",
              borderRadius: 2,
              width: { xs: "100%", md: 240 },
              "& .MuiOutlinedInput-root": {
                height: "36px",
                "& fieldset": { borderColor: COLORS.LIGHT_SECONDARY },
                "&:hover fieldset": { borderColor: COLORS.SECONDARY },
                "&.Mui-focused fieldset": { borderColor: COLORS.SECONDARY },
              },
            }}
          />
          <CustomTooltip title="Sort">
            <IconButton onClick={(e) => setSortAnchorEl(e.currentTarget)}>
              <Icon name="sort" color={COLORS.SECONDARY} />
            </IconButton>
          </CustomTooltip>
          <CustomTooltip title="View">
            <IconButton onClick={(e) => setViewAnchorEl(e.currentTarget)}>
              <Icon name="list" color={COLORS.SECONDARY} />
            </IconButton>
          </CustomTooltip>
          {isFilterApplied() && (
            <CustomTooltip title="Clear Filters">
              <IconButton onClick={clearFilters}>
                <Icon name="rotate" color={COLORS.ERROR} />
              </IconButton>
            </CustomTooltip>
          )}
        </Box>
      </Box>

      {/* Error Message */}
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper elevation={0} sx={{ backgroundColor: "transparent" }}>
        <TableContainer>
          <Table
            sx={{
              backgroundColor: "transparent",
              "& thead th": {
                backgroundColor: "transparent",
              },
              "& tbody td": {
                backgroundColor: "transparent",
                borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
              },
            }}
          >
            <TableHead>
              <TableRow>
                {[
                  { key: "Name", hasSort: true, width: "20%" },
                  { key: "Company Name", hasSort: true, width: "20%" },
                  { key: "Phone", hasSort: false, width: "15%" },
                  { key: "Status", hasSort: false, width: "15%" },
                  { key: "Joined On", hasSort: false, width: "15%" },
                  { key: "Actions", hasSort: false, width: "15%" },
                ].map((head) => (
                  <TableCell
                    key={head.key}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      color: COLORS.PRIMARY,
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      whiteSpace: "nowrap",
                      width: head.width,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <span>{head.key}</span>
                      {head.hasSort && (
                        <CustomTooltip title="Sort">
                          <Icon
                            name="sort"
                            color={COLORS.LIGHT_SECONDARY}
                            sx={{
                              fontSize: 16,
                              cursor: "pointer",
                              color:
                                head.key === "Name" && nameSort
                                  ? COLORS.ERROR
                                  : head.key === "Company Name" && companySort
                                    ? COLORS.ERROR
                                    : COLORS.LIGHT_SECONDARY,
                            }}
                            onClick={(e) => {
                              if (head.key === "Name") {
                                setNameSortAnchorEl(e.currentTarget);
                              } else if (head.key === "Company Name") {
                                setCompanySortAnchorEl(e.currentTarget);
                              }
                            }}
                          />
                        </CustomTooltip>
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && clientsData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : clientsData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography sx={{ color: COLORS.TEXT }}>
                      No clients found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                clientsData.map((client) => {
                  const creditsInfo = calculateRemainingCredits(client);
                  const frontendStatus = getFrontendStatus(client.status);
                  return (
                    <TableRow
                      key={client._id || client.id}
                      sx={{
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      }}
                    >
                      {/* Name */}
                      <TableCell sx={{ ...getRejectedCellStyle(client.status) }}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            alignItems: "center",
                          }}
                        >
                          <Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography
                                sx={{
                                  ...TYPOGRAPHY.BODY_LARGE,
                                  color: COLORS.TEXT,
                                  fontWeight: 600,
                                }}
                              >
                                {client.name || "N/A"}
                              </Typography>
                              <Chip
                                label={
                                  client.clientRole?.roleType ||
                                  client.roleType ||
                                  client.clientRoleType ||
                                  (client.companyName ? "Company" : "Individual")
                                }
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: "10px",
                                  fontWeight: 700,
                                  backgroundColor: (client.clientRole?.roleType || client.roleType || "").toLowerCase() === "individual"
                                    ? "rgba(66, 26, 110, 0.12)"
                                    : "rgba(33, 150, 243, 0.12)",
                                  color: (client.clientRole?.roleType || client.roleType || "").toLowerCase() === "individual"
                                    ? COLORS.PRIMARY
                                    : "#1976D2",
                                }}
                              />
                            </Box>
                            <Typography
                              sx={{
                                ...TYPOGRAPHY.BODY_SMALL,
                                color: COLORS.TEXT,
                              }}
                            >
                              {client.email || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Company */}
                      <TableCell
                        sx={{ color: COLORS.TEXT, ...TYPOGRAPHY.BODY_LARGE, ...getRejectedCellStyle(client.status) }}
                      >
                        {client.companyName || "-"}
                      </TableCell>

                      {/* Phone */}
                      <TableCell
                        sx={{ color: COLORS.TEXT, ...TYPOGRAPHY.BODY_LARGE, ...getRejectedCellStyle(client.status) }}
                      >
                        {client.phone || "-"}
                      </TableCell>

                      {/* Status */}
                      <TableCell sx={{ ...getRejectedCellStyle(client.status) }}>
                        <Chip
                          label={frontendStatus}
                          size="small"
                          sx={{
                            ...(statusStyles[client.status] ||
                              statusStyles.Pending),
                            ...TYPOGRAPHY.BODY_SMALL,
                            height: 22,
                          }}
                        />
                      </TableCell>

                      {/* Joined */}
                      <TableCell
                        sx={{ color: COLORS.TEXT, ...TYPOGRAPHY.BODY_LARGE, ...getRejectedCellStyle(client.status) }}
                      >
                        {formatDate(client.joinedAt || client.createdAt)}
                      </TableCell>



                      {/* Actions with Tooltips */}
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <CustomTooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(client)}
                            >
                              <Icon name="Action1" color={COLORS.TEXT} />
                            </IconButton>
                          </CustomTooltip>

                          <CustomTooltip title="Change Status">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedClient(client);
                                setStatusModalOpen(true);
                              }}
                              disabled={actionLoading}
                            >
                              <Icon name="Action2" color={COLORS.TEXT} />
                            </IconButton>
                          </CustomTooltip>

                          {client?.clientRole?.roleType !== "Individual" && (
                            <CustomTooltip title="Edit + Add Credits">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedClient(client);
                                  setEditCreditsOpen(true);
                                }}
                              >
                                <Icon name="Action3" color={COLORS.PRIMARY} />
                              </IconButton>
                            </CustomTooltip>
                          )}

                          <CustomTooltip title="Remove from View">
                            <IconButton
                              onClick={() => handleDeleteClick(client)}
                            >
                              <Icon name="Action4" color={COLORS.ERROR} />
                            </IconButton>
                          </CustomTooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {!loading && clientsData.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 2,
              p: 2,
            }}
          >
            <Typography sx={{ fontSize: 13, color: "#777" }}>
              {startIndex} - {endIndex} of {totalClients}
            </Typography>

            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                <Icon name="chevron-left" />
              </IconButton>

              {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                let pageNum;
                if (totalPages <= 3) {
                  pageNum = i + 1;
                } else if (page === 1) {
                  pageNum = i + 1;
                } else if (page === totalPages) {
                  pageNum = totalPages - 2 + i;
                } else {
                  pageNum = page - 1 + i;
                }

                if (pageNum > 0 && pageNum <= totalPages) {
                  return (
                    <Button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      sx={{
                        minWidth: 32,
                        height: 32,
                        bgcolor:
                          page === pageNum ? COLORS.PRIMARY : "transparent",
                        color: page === pageNum ? "#fff" : COLORS.TEXT,
                        border: page === pageNum ? "none" : "1px solid #E1BEE7",
                        "&:hover": {
                          bgcolor:
                            page === pageNum
                              ? COLORS.PRIMARY
                              : "rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      {pageNum}
                    </Button>
                  );
                }
                return null;
              })}

              <IconButton
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
              >
                <Icon name="chevron-right" />
              </IconButton>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Name Column Sort Menu */}
      <Menu
        anchorEl={nameSortAnchorEl}
        open={Boolean(nameSortAnchorEl)}
        onClose={() => setNameSortAnchorEl(null)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
            px: 0.5,
          },
        }}
      >
        {[
          { label: "A to Z", value: "AZ", icon: "foldUp" },
          { label: "Z to A", value: "ZA", icon: "foldDown" },
        ].map((item) => {
          const isActive = nameSort === item.value;

          return (
            <MenuItem
              key={item.value}
              onClick={() => handleNameSort(item.value)}
              sx={{
                gap: 1.5,
                borderRadius: 1,
                bgcolor: "transparent !important",
                "&:hover": { bgcolor: "transparent" },
                "&.Mui-selected": { bgcolor: "transparent" },
                color: isActive ? COLORS.PRIMARY : COLORS.TEXT,
              }}
            >
              <Icon
                name={item.icon}
                size={18}
                color={isActive ? COLORS.PRIMARY : COLORS.TEXT}
              />
              <Typography sx={TYPOGRAPHY.BODY_LARGE}>{item.label}</Typography>
            </MenuItem>
          );
        })}
      </Menu>

      {/* Company Column Sort Menu */}
      <Menu
        anchorEl={companySortAnchorEl}
        open={Boolean(companySortAnchorEl)}
        onClose={() => setCompanySortAnchorEl(null)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
            px: 0.5,
          },
        }}
      >
        {[
          { label: "A to Z", value: "AZ", icon: "foldUp" },
          { label: "Z to A", value: "ZA", icon: "foldDown" },
        ].map((item) => {
          const isActive = companySort === item.value;

          return (
            <MenuItem
              key={item.value}
              onClick={() => handleCompanySort(item.value)}
              sx={{
                gap: 1.5,
                borderRadius: 1,
                bgcolor: "transparent !important",
                "&:hover": { bgcolor: "transparent" },
                "&.Mui-selected": { bgcolor: "transparent" },
                color: isActive ? COLORS.PRIMARY : COLORS.TEXT,
              }}
            >
              <Icon
                name={item.icon}
                size={18}
                color={isActive ? COLORS.PRIMARY : COLORS.TEXT}
              />
              <Typography sx={TYPOGRAPHY.BODY_LARGE}>{item.label}</Typography>
            </MenuItem>
          );
        })}
      </Menu>

      {/* View Menu */}
      <Menu
        anchorEl={viewAnchorEl}
        open={Boolean(viewAnchorEl)}
        onClose={() => setViewAnchorEl(null)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
            px: 0.5,
          },
        }}
      >
        {[
          {
            label: "All",
            value: "ALL",
            icon: "usersGroup",
            color: COLORS.PRIMARY,
          },
          {
            label: "Active",
            value: "APPROVED",
            icon: "users",
            color: "#8CD47E",
          },
          {
            label: "Pending",
            value: "PENDING",
            icon: "users",
            color: "#F8D66D",
          },
          {
            label: "Rejected",
            value: "REJECTED",
            icon: "users",
            color: "#FF6961",
          },
        ].map((item) => {
          const isActive = selectedView === item.value;

          return (
            <MenuItem
              key={item.value}
              onClick={() => {
                setSelectedView(item.value);
                setViewAnchorEl(null);
              }}
              sx={{
                gap: 1.5,
                borderRadius: 1,
                bgcolor: "transparent !important",
                "&:hover": { bgcolor: "transparent" },
                "&.Mui-selected": { bgcolor: "transparent" },
                color: isActive ? item.color : COLORS.TEXT,
              }}
            >
              <Icon name={item.icon} size={18} color={item.color} />
              <Typography sx={TYPOGRAPHY.BODY_LARGE}>{item.label}</Typography>
            </MenuItem>
          );
        })}
      </Menu>

      {/* Global Sort Menu */}
      <Menu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={() => setSortAnchorEl(null)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
            px: 0.5,
          },
        }}
      >
        {[
          { label: "Latest First", value: "LATEST", icon: "foldUp" },
          { label: "Oldest First", value: "OLDEST", icon: "foldDown" },
          { label: "A to Z", value: "AZ", icon: "foldUp" },
          { label: "Z to A", value: "ZA", icon: "foldDown" },
          { label: "Credits High", value: "CREDITS_HIGH", icon: "foldUp" },
          { label: "Credits Low", value: "CREDITS_LOW", icon: "foldDown" },
        ].map((item) => {
          const isActive = selectedSort === item.value;

          return (
            <MenuItem
              key={item.value}
              onClick={() => {
                setSelectedSort(item.value);
                setSortAnchorEl(null);
              }}
              sx={{
                gap: 1.5,
                borderRadius: 1,
                bgcolor: "transparent !important",
                "&:hover": { bgcolor: "transparent" },
                "&.Mui-selected": { bgcolor: "transparent" },
                color: isActive ? COLORS.PRIMARY : COLORS.TEXT,
              }}
            >
              <Icon
                name={item.icon}
                size={18}
                color={isActive ? COLORS.PRIMARY : COLORS.TEXT}
              />
              <Typography sx={TYPOGRAPHY.BODY_LARGE}>{item.label}</Typography>
            </MenuItem>
          );
        })}
      </Menu>

      {/* Client Details Sidebar */}
      <ClientDetailsSidebar
        open={isSidebarOpen}
        onClose={handleCloseSidebar}
        client={selectedClient}
      />

      <ClientDetailsPopup
        open={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        client={selectedClient}
      />

      {/* Status Update Modal */}
      <ClientStatusUpdateModal
        open={statusModalOpen}
        client={selectedClient}
        fromStatus={
          selectedClient ? getFrontendStatus(selectedClient.status) : "Pending"
        }
        toStatus={
          selectedClient && selectedClient.status === "PENDING"
            ? "Active"
            : "Rejected"
        }
        onClose={() => {
          setStatusModalOpen(false);
          setSelectedClient(null);
        }}
        onUpdate={handleStatusUpdate} // Changed to onUpdate
        hideCredits={isTechnicalFlow}
      />

      {/* Add Credits Modal */}
      <AddCreditsModal
        open={addCreditsOpen}
        onClose={() => setAddCreditsOpen(false)}
        client={selectedClient}
        onApprove={(client, credits, amount) => {
          fetchClients();
        }}
        showToast={showToast}
      />

      {/* Delete Modal */}
      <ClientDeleteModal
        open={deleteOpen}
        client={selectedClient}
        onClose={() => setDeleteOpen(false)}
        onConfirmDelete={handleConfirmDelete}
      />

      <EditCreditsModal
        open={editCreditsOpen}
        onClose={() => setEditCreditsOpen(false)}
        client={selectedClient}
        onUpdate={(client, action, credits, reason) => {
          fetchClients(); // Refresh the client list
        }}
        showToast={showToast}
      />

      {/* Toast Component - Rendered at parent level */}
      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={hideToast}
        autoHideDuration={5000}
      />
    </Box>
  );
};

export default Clients;
